import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TrendingUp, ArrowUpRight, Newspaper } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const DAYS_OPTIONS = [
  { v: 14, label: "Last 14 days" },
  { v: 30, label: "Last 30 days" },
  { v: 60, label: "Last 60 days" },
  { v: 90, label: "Last 90 days" },
  { v: 0, label: "All time" },
];

const BAR_COLORS = ["#0F1F3D", "#334155", "#475569", "#64748B", "#94A3B8"];

function RankedBars({ title, subtitle, data, testid }) {
  const rows = (data || [])
    .map((d) => ({
      name: d.term,
      recent: d.recent || 0,
      total: d.total || 0,
      delta: d.delta || 0,
    }))
    .sort(
      (a, b) =>
        b.total - a.total ||
        b.delta - a.delta ||
        b.recent - a.recent ||
        a.name.localeCompare(b.name),
    )
    .slice(0, 8);
  return (
    <div
      className="rounded-2xl border border-[#E2E8F0] bg-white p-5"
      data-testid={testid}
    >
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm font-bold text-[#0F1F3D]">{title}</h3>
      </div>
      <p className="text-xs text-[#94A3B8] mb-4">{subtitle}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-[#94A3B8] py-6 text-center">
          Not enough data yet.
        </p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((r, i) => {
            const max = Math.max(...rows.map((x) => x.total), 1);
            const pct = Math.round((r.total / max) * 100);
            return (
              <div key={r.name} className="flex items-center gap-3">
                <div
                  className="w-36 truncate text-[13px] text-[#334155] font-medium"
                  title={r.name}
                >
                  {r.name}
                </div>
                <div className="flex-1 h-6 rounded-md bg-[#F1F5F9] overflow-hidden relative">
                  <div
                    className="h-full rounded-md"
                    style={{
                      width: `${Math.max(pct, 6)}%`,
                      background: BAR_COLORS[i % BAR_COLORS.length],
                    }}
                  />
                </div>
                <div className="w-16 text-right text-[13px] text-[#0F1F3D] font-semibold tabular-nums">
                  {r.total}
                  {r.delta > 0 && (
                    <span className="ml-1 inline-flex items-center text-[#059669] text-[11px] font-bold">
                      <ArrowUpRight className="h-3 w-3" />
                      {r.delta}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Trends() {
  const { user } = useAuth();
  const isOrgMember = user?.role === "org_member";
  const [searchParams, setSearchParams] = useSearchParams();
  const urlProject = searchParams.get("project") || "";
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(urlProject);
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const r = await api.get("/projects");
        const list = r.data?.projects || [];
        setProjects(list);
        if (list.length > 0) {
          const preferred = list.find((p) => p.id === urlProject);
          setProjectId(preferred ? preferred.id : list[0].id);
        } else {
          setLoading(false);
        }
      } catch {
        setError("Failed to load projects");
        setLoading(false);
      }
    };
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("project", projectId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const r = await api.get("/trending", {
          params: { project_id: projectId, days },
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled)
          setError(e?.response?.data?.detail || "Failed to load trends");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId, days]);

  const series = useMemo(
    () => (data?.series || []).map((d) => ({ ...d, label: d.date.slice(5) })),
    [data],
  );

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1F3D] flex items-center gap-2">
            <TrendingUp className="h-6 w-6 text-[#0F1F3D]" /> Trends
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Understand what&apos;s changing across your project.
          </p>
        </div>
      </div>

      {projects.length > 0 && (
        <div className="flex items-center justify-start gap-6 border-b py-2 border-[#E2E8F0] mb-6">
          <div className="flex flex-wrap items-center gap-2.5">
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              data-testid="trending-project-select"
              className="px-4 rounded-lg border border-[#CBD5E1] bg-white text-base font-semibold text-[#0F1F3D] min-h-[40px] min-w-[220px] focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 shadow-sm"
            >
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              data-testid="trending-days-select"
              className="px-3 rounded-md border border-[#E2E8F0] bg-white text-sm min-h-[40px]"
            >
              {DAYS_OPTIONS.map((d) => (
                <option key={d.v} value={d.v}>
                  {d.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {projects.length === 0 && !loading ? (
        <div
          className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-10 text-center"
          data-testid="trending-empty"
        >
          <p className="text-[#64748B]">No projects yet.</p>
          {!isOrgMember && (
            <Link
              to="/app/projects/new"
              data-testid="trending-create-project"
              className="text-[#0F1F3D] font-semibold hover:underline focus:outline-none focus:ring-2 focus:ring-[#0F1F3D]/20 rounded-sm"
            >
              Click Here to create your first project
            </Link>
          )}
        </div>
      ) : !projectId ? (
        <div
          className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-10 text-center"
          data-testid="trending-pick-project"
        >
          <p className="text-[#64748B]">
            Select a project above to view its trends.
          </p>
        </div>
      ) : loading ? (
        <div className="p-10 text-[#64748B]" data-testid="trending-loading">
          Loading trends…
        </div>
      ) : error ? (
        <div
          className="p-6 rounded-xl bg-[#FEF2F2] text-[#B91C1C]"
          data-testid="trending-error"
        >
          {error}
        </div>
      ) : !data ? (
        <div className="p-10 text-[#64748B]" data-testid="trending-loading">
          Loading trends…
        </div>
      ) : (
        <div className="space-y-5" data-testid="trending-content">
          <div className="rounded-2xl border border-[#E2E8F0] bg-white p-5">
            <div className="flex items-baseline justify-between mb-4">
              <h3 className="text-sm font-bold text-[#0F1F3D]">
                Coverage volume
              </h3>
              <span className="text-xs text-[#94A3B8]">
                {data.total_items} items ·{" "}
                {data.all_time ? "all time" : `last ${data.days} days`}
              </span>
            </div>
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <LineChart
                  data={series}
                  margin={{ top: 5, right: 10, left: -18, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 11, fill: "#94A3B8" }}
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: "#94A3B8" }}
                    allowDecimals={false}
                  />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="All items"
                    stroke="#0F1F3D"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="high"
                    name="High priority"
                    stroke="#EF4444"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <RankedBars
              title="Rising keywords"
              subtitle="Ranked by momentum (last 7 days vs earlier). Green = rising."
              data={data.keywords}
              testid="trending-keywords"
            />
            <RankedBars
              title="Most-mentioned entities"
              subtitle="Companies, ministries and people across covered stories."
              data={data.entities}
              testid="trending-entities"
            />
         
          </div>
             <div
              className="rounded-2xl border border-[#E2E8F0] bg-white p-5"
              data-testid="trending-sources"
            >
              <h3 className="text-sm font-bold text-[#0F1F3D] mb-4 flex items-center gap-2">
                <Newspaper className="h-4 w-4 text-[#0F1F3D]" /> Top sources
              </h3>
              {(data.top_sources || []).length === 0 ? (
                <p className="text-sm text-[#94A3B8]">No source data yet.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {data.top_sources.map((s) => (
                    <span
                      key={s.term}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F1F5F9] text-[13px] text-[#334155]"
                    >
                      {s.term}
                      <span className="font-bold text-[#0F1F3D]">
                        {s.total}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
        </div>
      )}
    </div>
  );
}